import { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactComponent as AddIcon } from "../../../../../assets/icons/fi-plus-circle.svg";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { contractService } from "../../../../../services/api/contractService";
import Loading from "../../../components/Loading";
import ContractPopup from "../Popup/AddPopup";

const TabContractInfo = () => {
  const { id } = useParams();
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [popupMode, setPopupMode] = useState(null); // "create" | "edit" | null
  const [currentDocument, setCurrentDocument] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await contractService.get_by_customer_id(id, search, token);
      setDocuments(res.data?.listContract || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server.",
      );
    } finally {
      setLoading(false);
    }
  }, [id, token, search]);

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    if (searchInput) params.set("search", searchInput);
    else params.delete("search");
    setSearchParams(params);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleAddDocument = async (newDocument) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", newDocument.name);
      formData.append("pdfFile", newDocument.pdfFile);
      await contractService.create(id, formData, token);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Lỗi khi tạo hợp đồng.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa hợp đồng này?")) return;

    try {
      setLoading(true);
      await contractService.delete(docId, token);
      toast.success("Xoá hợp đồng thành công!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Lỗi khi xoá hợp đồng.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDocument = async (docId, updated) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", updated.name);
      formData.append("pdfFile", updated.pdfFile);
      await contractService.update(docId, formData, token);
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Lỗi khi cập nhật hợp đồng.",
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => {
    setPopupMode(null);
    setCurrentDocument(null);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="pb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-6 w-3/5 lap:2/5">
          <div className="relative w-full flex items-center">
            <input
              type="text"
              placeholder="Tìm kiếm hợp đồng"
              autoComplete="off"
              className="border border-gray-200 rounded-lg px-4 py-2 focus:outline-blue-500 w-full"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <span
              className="absolute right-3 cursor-pointer"
              onClick={handleSearch}
            >
              <img src="/icons/search-black.svg" alt="Search icon" />
            </span>
          </div>
        </div>
        <button
          onClick={() => setPopupMode("create")}
          className="bg-[#0457C2] h-[40px] hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-be-vietnam-medium flex items-center justify-center gap-2"
        >
          <AddIcon />
          Thêm mới
        </button>
      </div>

      {loading ? (
        <div className="flex h-full items-center justify-center mt-10">
          <Loading />
        </div>
      ) : (
        <div className="space-y-4">
          {documents.length > 0 ? (
            documents.map((doc) => (
              <div key={doc._id} className="flex gap-2 items-center">
                <div className="w-3/5 bg-white cursor-pointer rounded-lg flex items-center justify-between px-5 py-[13.5px] border border-[#EAEAEA] hover:bg-[#f9f9f9]">
                  {/* LEFT */}
                  <div className="flex gap-2 items-center">
                    <img
                      src="/images/document.png"
                      alt="icon"
                      className="w-5 h-5"
                    />
                    <p className="text-gray-700 text-sm font-sf-medium uppercase">
                      {doc.name}
                    </p>
                  </div>

                  {/* RIGHT – chỉ hiện khi có pdfUrl */}
                  {doc.pdfUrl ? (
                    <div className="flex items-center gap-2">
                      <img
                        src="/images/pdf-icon.png"
                        alt="PDF icon"
                        className="w-6 h-6"
                      />
                      <a
                        href={doc.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Mở file PDF
                      </a>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">
                      Chưa có file PDF
                    </span>
                  )}
                </div>

                {/* EDIT */}
                <img
                  src="/icons/edit-black.svg"
                  alt="Edit icon"
                  className="w-5 h-5 cursor-pointer"
                  onClick={() => {
                    setCurrentDocument(doc);
                    setPopupMode("edit");
                  }}
                />

                {/* DELETE */}
                {user?.role === "SuperAdmin" && (
                  <img
                    src="/icons/trash-can.svg"
                    alt="Delete icon"
                    className="w-5 h-5 cursor-pointer"
                    onClick={() => handleDelete(doc._id)}
                  />
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500">Không có hợp đồng nào để hiển thị.</p>
          )}
        </div>
      )}

      {/* Single Popup Component */}
      {popupMode && (
        <ContractPopup
          mode={popupMode}
          document={currentDocument}
          closePopup={closePopup}
          handleAddDocument={handleAddDocument}
          handleEditDocument={handleEditDocument}
        />
      )}
    </div>
  );
};

export default TabContractInfo;
